import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, FormControlLabel, LinearProgress, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import financeService from '../../services/financeService';
import { ACCOUNT_TYPE_LABEL, LedgerAccount, TrialBalance } from '../../types/finance';
import { useTimezone } from '../../hooks/useTimezone';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import Amount from '../../components/finance/ledger/Amount';
import AccountLedgerLink from '../../components/finance/ledger/AccountLedgerLink';
import LedgerControlsCard from '../../components/finance/ledger/LedgerControlsCard';

/** Rows worth showing: accounts with a balance, and the groups above them. */
const visibleRows = (rows: LedgerAccount[], hideZero: boolean): LedgerAccount[] => {
  if (!hideZero) return rows;
  const keep = new Set<string>();
  const byCode = new Map(rows.map(r => [r.code, r]));
  rows.filter(r => !r.isGroup && r.netPaisa !== 0).forEach(r => {
    let cur: LedgerAccount | undefined = r;
    while (cur) { keep.add(cur.code); cur = cur.parentCode ? byCode.get(cur.parentCode) : undefined; }
  });
  return rows.filter(r => keep.has(r.code));
};

/**
 * Finance → Trial balance. Every account's balance on a date, debit or
 * credit — the proof the books balance. Totals come from the server.
 */
const TrialBalancePage: React.FC = () => {
  const theme = useTheme();
  const { getToday, formatDate } = useTimezone();
  const money = useCurrencyFormat();
  const [asOf, setAsOf] = useState(getToday());
  const [hideZero, setHideZero] = useState(true);
  const [data, setData] = useState<TrialBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    financeService.getTrialBalance(asOf)
      .then(d => { if (live) { setData(d); setError(null); } })
      .catch((e: any) => { if (live) setError(e?.message || 'Could not load the trial balance.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [asOf]);

  const rows = useMemo(() => visibleRows(data?.rows ?? [], hideZero), [data, hideZero]);
  const totals = data?.totals;
  const empty = !!data && rows.length === 0;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Trial balance</Typography>
        <Typography variant="body2" color="text.secondary">
          Every account's balance on a date. Total debits must equal total credits.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField
          label="As of"
          type="date"
          size="small"
          value={asOf}
          onChange={e => e.target.value && setAsOf(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        <FormControlLabel
          control={<Switch checked={hideZero} onChange={e => setHideZero(e.target.checked)} size="small" />}
          label={<Typography variant="body2">Hide zero balances</Typography>}
        />
        <Box sx={{ flex: 1 }} />
        {totals && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 1.75, py: 0.75, borderRadius: 2,
              bgcolor: alpha(totals.balanced ? theme.palette.success.main : theme.palette.error.main, 0.08),
              color: totals.balanced ? 'success.dark' : 'error.dark',
            }}
          >
            {totals.balanced ? <CheckCircle fontSize="small" /> : <ErrorOutline fontSize="small" />}
            <Typography variant="body2" fontWeight={600}>
              {totals.balanced ? 'Books balance' : `Out of balance by ${money(Math.abs(totals.debitPaisa - totals.creditPaisa) / 100)}`}
            </Typography>
          </Stack>
        )}
      </Stack>
      </Paper>

      {/* The control compares today's challans with today's ledger; it has no past view. */}
      {asOf === getToday() && <LedgerControlsCard />}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
        {loading && data && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { py: 1 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell>Account</TableCell>
                <TableCell sx={{ width: 170 }} align="right">Debit</TableCell>
                <TableCell sx={{ width: 170 }} align="right">Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !data ? <TableRowSkeletons columns={3} rows={10} rowHeight={36} /> : rows.map(r => {
                if (r.depth === 0) {
                  return (
                    <TableRow key={r._id}>
                      <TableCell colSpan={3} sx={{ pt: 2.5, borderBottom: 0 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                          {ACCOUNT_TYPE_LABEL[r.type]}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={r._id} hover={!r.isGroup}>
                    <TableCell sx={{ pl: 2 + (r.depth - 1) * 2.5 }}>
                      <AccountLedgerLink accountId={r._id} window={{ to: data?.asOf }} code={r.code} name={r.name} bold={r.isGroup} />
                    </TableCell>
                    <TableCell align="right">{!r.isGroup && <Amount paisa={Math.max(r.netPaisa, 0)} />}</TableCell>
                    <TableCell align="right">{!r.isGroup && <Amount paisa={Math.max(-r.netPaisa, 0)} />}</TableCell>
                  </TableRow>
                );
              })}
              {empty && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No balances as of {formatDate(`${asOf}T12:00:00`)}.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {totals && (
                <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', borderBottom: 0, py: 1.5 } }}>
                  <TableCell><Typography variant="body2" fontWeight={700}>Total</Typography></TableCell>
                  <TableCell align="right"><Amount paisa={totals.debitPaisa} blankZero={false} fontWeight={700} /></TableCell>
                  <TableCell align="right"><Amount paisa={totals.creditPaisa} blankZero={false} fontWeight={700} /></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TrialBalancePage;
